import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wrench, Sparkles, Grid3X3, Layers, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { SERVICES_DATA } from "@/data/services";

const ICONS = { Layers, Wrench, Sparkles, Grid3X3 };

export const Services = () => (
  <section id="leistungen" data-testid="services-section" className="relative bg-ink py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-5 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-16 md:mb-24"
      >
        <p className="mb-4 text-xs font-bold tracking-[0.3em] text-brand-bright">UNSERE LEISTUNGEN</p>
        <h2 className="max-w-3xl font-display text-3xl sm:text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white leading-[1.02] hyphens-auto">
          Boden&shy;beschichtungen&#160;&<br />
          <span className="text-white/40">Gebäudeservice aus einer Hand.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-white/55">
          Unser Schwerpunkt: hochwertige Epoxidharzböden für Garage, Keller, Werkstatt und Gewerbe.
          Ergänzt durch Hausmeisterservice, Gebäudereinigung und Pflasterreinigung – für Privat-
          und Gewerbekunden in Gronau (Westf.) und Umgebung im Kreis Borken.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES_DATA.map((s, i) => {
          const Icon = ICONS[s.icon];
          if (s.featured) {
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6 }}
                className="sm:col-span-2"
              >
                <Link
                  data-testid={`service-card-${s.slug}`}
                  to={`/${s.slug}`}
                  className="group relative flex h-full min-h-[340px] md:min-h-[380px] flex-col justify-end overflow-hidden rounded-2xl border border-brand/40 p-8 md:p-10 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-brand"
                >
                  <img
                    src={s.image}
                    alt={s.imageAlt}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />
                  <span className="absolute left-8 top-8 rounded-full bg-brand px-4 py-1.5 text-[10px] font-extrabold tracking-[0.2em] text-black">
                    UNSERE KERNLEISTUNG
                  </span>
                  <div className="relative">
                    <h3 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
                      {s.name}
                    </h3>
                    <p className="mt-3 max-w-md text-sm md:text-base leading-relaxed text-white/75">{s.short}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-brand-bright group-hover:text-white transition-colors duration-300">
                      MEHR ERFAHREN
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          }
          return (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
            >
              <Link
                data-testid={`service-card-${s.slug}`}
                to={`/${s.slug}`}
                className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-white/10 bg-surface p-8 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-brand/50"
              >
                <div>
                  <span className="mb-7 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-brand/10 text-brand-bright ring-1 ring-brand/30 transition-colors duration-500 group-hover:bg-brand group-hover:text-black">
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight text-white">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{s.short}</p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-brand-bright group-hover:text-white transition-colors duration-300">
                  MEHR ERFAHREN
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className="relative mt-5 flex min-h-[260px] md:min-h-[320px] items-end overflow-hidden rounded-2xl border border-white/10"
      >
        <img
          src="/assets/img/haus-grundstueck-pflege-gronau.jpg"
          alt="Gepflegtes Wohnhaus mit Grundstück – Gebäudeservice und Pflege von Terbeck Service in Gronau"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="relative flex w-full flex-col sm:flex-row sm:items-end sm:justify-between gap-6 p-8 md:p-12">
          <p className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white leading-[1.05]">
            Für Privat & Gewerbe –<br />alles aus einer Hand.
          </p>
          <button
            data-testid="services-banner-cta"
            onClick={() => scrollToId("kontakt")}
            className="shrink-0 whitespace-nowrap rounded-full bg-brand px-8 py-4 text-sm font-bold tracking-wider text-black hover:bg-brand-bright transition-colors duration-300"
          >
            ANGEBOT ANFRAGEN
          </button>
        </div>
      </motion.div>
    </div>
  </section>
);
